import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ACTION_TONES } from '@claims/core';
import { KEY_WORDS, TONE_KEY_LEAD, TONE_KEY_WORDS, type KeyEntry, type KeyMark } from './row-key.js';
import { InOutBadge } from './InOutBadge.js';

/** THE ROW KEY, THE CIRCLE I (ported 2026-10-18 from the medrec console's components/list/RowKey.tsx, its design plan's Lane 1; DESIGN_SYSTEM
 *  8; Rod there: "each row I'll need a circle i to explain what the inside may mean"). A 24 px circle with an italic "i": its name "Key for
 *  <client> row", its hover "What this row's marks mean". A press opens the pop-over under it, opening leftward: `role="dialog"` named by
 *  its title, the focus on it; Esc closes it and gives the focus back to the button, and so does a click outside. ONE KEY IS OPEN AT A TIME
 *  (opening another closes this one). It lists only the marks the row carries (`entries`, from Sweet Claims' dictionary `list/row-key.ts`),
 *  each drawn as it appears on the row with its plain words, and ends with the colours' footnote: the buttons' colour key (`ToneKey`, ported
 *  2026-10-25 with the medrec console's action tones), or a screen's own. No single-letter key opens it (Rod 10-17, 1b: no keyboard
 *  shortcuts); Tab, Enter and Esc work as everywhere. */

/** Which key is open, across the page: one at a time. */
let openKey: string | null = null;
const listeners = new Set<() => void>();
const setOpenKey = (id: string | null): void => { openKey = id; for (const l of listeners) l(); };
const subscribe = (l: () => void): (() => void) => { listeners.add(l); return () => listeners.delete(l); };

const WIDTH = 392;

export function RowKey({ name, title = KEY_WORDS.title, entries, testid = 'row-key', footnote }: {
  /** The button's accessible name ("Key for Zz Test row", `KEY_WORDS.buttonName`). */
  name: string; title?: string; entries: readonly KeyEntry[]; testid?: string;
  /** The colours' line the key ends with: the buttons' colour key by default, a screen's own where its colours mean something else, null for
   *  none. */
  footnote?: string | null;
}) {
  const id = useId();
  const open = useSyncExternalStore(subscribe, () => openKey === id, () => false);
  const button = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const titleId = `${id}-t`;
  useLayoutEffect(() => {
    if (!open) return;
    const b = button.current?.getBoundingClientRect();
    if (b === undefined) return;
    const left = Math.max(8, Math.min(Math.round(b.right - WIDTH), window.innerWidth - WIDTH - 8));
    setAt({ top: Math.round(b.bottom + 6), left });
  }, [open]);
  const drawn = open && at !== null;
  useEffect(() => {
    if (!drawn) return;
    box.current?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node | null;
      if (t !== null && (box.current?.contains(t) || button.current?.contains(t))) return;
      setOpenKey(null);
    };
    // The box is fixed to the window under its button, and this console scrolls the page itself (the medrec console scrolls a region): a
    // resize, or a scroll that moves the row away from the box (more than a few px), would leave it behind, so either closes it.
    const startY = window.scrollY;
    const onResize = (): void => setOpenKey(null);
    const onScroll = (): void => { if (Math.abs(window.scrollY - startY) > 16) setOpenKey(null); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('resize', onResize); window.removeEventListener('scroll', onScroll); };
  }, [drawn]);
  useEffect(() => () => { if (openKey === id) setOpenKey(null); }, [id]);
  const close = (): void => { setOpenKey(null); button.current?.focus(); };
  return (
    <>
      <button
        ref={button}
        type="button"
        className={open ? 'lp-ib lp-on' : 'lp-ib'}
        aria-label={name}
        title={KEY_WORDS.buttonHover}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); setOpenKey(open ? null : id); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); if (e.key === 'Escape' && open) { e.preventDefault(); close(); } }}
      >
        <span aria-hidden="true">i</span>
      </button>
      {drawn ? (
        <div
          ref={box}
          className="lp-key"
          role="dialog"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={{ top: at.top, left: at.left }}
          data-testid={`${testid}-box`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') { e.preventDefault(); close(); }
          }}
        >
          <h4 className="lp-key-h" id={titleId}>
            {title}
            <span className="lp-key-x">{KEY_WORDS.escCloses}</span>
          </h4>
          <dl className="lp-key-dl">
            {entries.map((e) => (
              <div key={e.id} className="lp-key-row" data-key-entry={e.id}>
                <dt><Mark mark={e.mark} /></dt>
                <dd>{e.words}</dd>
              </div>
            ))}
          </dl>
          {footnote === null ? null : <p className="lp-key-foot" data-testid={`${testid}-foot`}>{footnote === undefined ? <ToneKey /> : footnote}</p>}
        </div>
      ) : null}
    </>
  );
}

/** THE BUTTONS' COLOUR KEY (ported 2026-10-25 from the medrec console's RowKey, its PR #356), the shared footnote: "Buttons: red pays, amber to
 *  the client, blue to the carrier, green settles records, grey looks, hands off or says no" (a middle dot between the parts), each colour's
 *  word drawn in its own tone (action-tones.css), so the key reads in the colours it names. A colour's word holds to the word after it (a
 *  no-break space). The words a screen reader reads are `KEY_WORDS.colours`, the same line. */
export function ToneKey() {
  return (
    <>
      {TONE_KEY_LEAD}
      {ACTION_TONES.map((t, i) => (
        <Fragment key={t}>
          {i === 0 ? ' ' : ' \u{00B7} '}
          <b data-action-tone={t}>{TONE_KEY_WORDS[t].colour}</b>{`\u{00A0}${TONE_KEY_WORDS[t].does}`}
        </Fragment>
      ))}
    </>
  );
}

/** An entry's mark, drawn as it appears on the row. */
function Mark({ mark }: { mark: KeyMark }) {
  switch (mark.kind) {
    case 'text': return <span className={mark.tone === undefined ? 'lp-key-t' : `lp-key-t lp-${mark.tone}`}>{mark.text}</span>;
    case 'tag': return <span className="lp-tag">{mark.icon ? <span aria-hidden="true">{mark.icon}</span> : null}{mark.words}</span>;
    case 'links': return <span className="lp-links lp-links-key">{mark.letters.map((l) => <span key={l} className="lp-lk">{l}</span>)}</span>;
    case 'io': return mark.text === undefined ? <InOutBadge kind={mark.io} /> : <span className="lp-key-io"><InOutBadge kind={mark.io} word={false} /><span className="lp-key-t">{mark.text}</span></span>;
  }
}
